import { apiClient } from './client';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface Petition {
  id: string;
  caseId: string;
  petitionType: string;
  status: 'draft' | 'generating' | 'in_progress' | 'completed' | 'done' | 'failed' | 'submitted' | 'approved' | 'rejected';
  content: {
    petition?: string;
    cover_letter?: string;
    coverLetter?: string;
    work_plans?: string;
    workPlans?: string;
    conclusions?: string;
    sections?: Record<string, string>;
    evidences_collection?: string;
    lors_collection?: string;
  };
  createdAt: string;
  updatedAt: string;
  metadata?: {
    beneficiaryName?: string;
    visaType?: string;
    wordCount?: number;
    progress?: number;
    currentStage?: string;
  };
}

export interface GeneratePetitionOptions {
  petitionType?: string;  // e.g., 'EB-1A', 'EB-1B', 'EB-1C', 'EB-2', 'EB-2 NIW', 'O-1A', 'O-1B'
  customPrompt?: string;  // Custom instructions for AI generation
  templateId?: string;
  exhibitListId?: string; // ID of exhibit list to use for this petition
  includeExhibitIndex?: boolean; // Include exhibit index section in the petition
}

export interface ExhibitList {
  id: string;
  caseId: string;
  title: string;
  summary: string;
  visaType: string;
  totalExhibits: number;
  createdAt: string;
  updatedAt: string;
}

export interface FactCheckClaim {
  claim: string;
  location: {
    start: number;
    end: number;
    context: string;
  };
  type: 'factual' | 'statistical' | 'citation' | 'achievement' | 'date' | 'credential';
  severity: 'critical' | 'moderate' | 'minor';
}

export interface FactCheckIssue {
  claim: FactCheckClaim;
  issueType: 'missing_evidence' | 'inconsistency' | 'unsupported' | 'contradictory' | 'vague' | 'citation_missing';
  description: string;
  suggestion: string;
  relatedExhibits?: string[];
  confidence: number;
}

export interface FactCheckResult {
  overallScore: number;
  totalClaims: number;
  verifiedClaims: number;
  issues: FactCheckIssue[];
  suggestions: string[];
  summary: string;
  categories: {
    factual: { total: number; verified: number; issues: number };
    citations: { total: number; verified: number; missing: number };
    consistency: { score: number; issues: number };
    evidence: { adequacy: number; gaps: string[] };
  };
}

class PetitionService {
  /**
   * Get all petitions for a case
   */
  static async getPetitionsForCase(caseId: string): Promise<ApiResponse<Petition[]>> {
    return apiClient.get(`/petitions/case/${caseId}`);
  }

  /**
   * Get latest petition for a case
   */
  static async getLatestPetition(caseId: string): Promise<ApiResponse<Petition>> {
    return apiClient.get(`/petitions/case/${caseId}/latest`);
  }

  /**
   * Get petition by ID
   */
  static async getPetition(petitionId: string): Promise<ApiResponse<Petition>> {
    return apiClient.get(`/petitions/${petitionId}`);
  }

  /**
   * Generate new petition for a case
   */
  static async generatePetition(
    caseId: string,
    options?: GeneratePetitionOptions
  ): Promise<ApiResponse<{ petitionId: string; jobId: string | null }>> {
    const body: any = {
      projectId: caseId,
    };

    if (options?.petitionType) {
      body.petitionType = options.petitionType;
    }

    if (options?.customPrompt) {
      body.customPrompt = options.customPrompt;
    }

    if (options?.templateId) {
      body.templateId = options.templateId;
    }

    if (options?.exhibitListId) {
      body.exhibitListId = options.exhibitListId;
    }

    if (options?.includeExhibitIndex !== undefined) {
      body.includeExhibitIndex = options.includeExhibitIndex;
    }

    return apiClient.post('/petitions/generate', body);
  }

  /**
   * Get exhibit lists for a case
   */
  static async getExhibitListsForCase(caseId: string): Promise<ApiResponse<ExhibitList[]>> {
    return apiClient.get(`/case/${caseId}/exhibit-lists`);
  }

  /**
   * Get petition generation status
   */
  static async getPetitionStatus(petitionId: string): Promise<ApiResponse<{
    status: string;
    progress: number;
    currentStage?: string;
    error?: string;
    content?: unknown;
  }>> {
    return apiClient.get(`/petitions/${petitionId}/status`);
  }

  /**
   * Delete petition
   */
  static async deletePetition(petitionId: string): Promise<ApiResponse> {
    return apiClient.delete(`/petitions/${petitionId}`);
  }

  /**
   * Download petition as PDF
   */
  static async downloadPetition(
    petitionId: string,
    format: 'pdf' = 'pdf'
  ): Promise<Blob> {
    const params = new URLSearchParams({ format });

    return apiClient.getBlob(`/petitions/${petitionId}/download?${params.toString()}`);
  }

  /**
   * Download petition as DOCX for Word
   */
  static async downloadToWord(petitionId: string): Promise<Blob> {
    return apiClient.getBlob(`/petitions/drafting/${petitionId}/export/docx`);
  }

  /**
   * Download petition as DOCX for Google Docs
   */
  static async downloadForGoogleDocs(petitionId: string): Promise<Blob> {
    return apiClient.getBlob(`/petitions/drafting/${petitionId}/export/google-docs`);
  }

  /**
   * Submit petition for review
   */
  static async submitPetition(petitionId: string): Promise<ApiResponse<Petition>> {
    return apiClient.post(`/petitions/${petitionId}/submit`, {});
  }

  /**
   * Approve petition
   */
  static async approvePetition(petitionId: string): Promise<ApiResponse<Petition>> {
    return apiClient.post(`/petitions/${petitionId}/approve`, {});
  }

  /**
   * Reject petition
   */
  static async rejectPetition(petitionId: string, reason?: string): Promise<ApiResponse<Petition>> {
    return apiClient.post(`/petitions/${petitionId}/reject`, { reason });
  }

  /**
   * Get evidence for petition
   */
  static async getPetitionEvidence(petitionId: string): Promise<ApiResponse<any[]>> {
    return apiClient.get(`/petitions/${petitionId}/evidence`);
  }

  /**
   * Add evidence to petition
   */
  static async addEvidenceToPetition(petitionId: string, documentIds: string[]): Promise<ApiResponse> {
    return apiClient.post(`/petitions/${petitionId}/evidence`, { documentIds });
  }

  /**
   * Update a specific petition section
   */
  static async updatePetitionSection(
    petitionId: string,
    sectionKey: string,
    updates: { content: string }
  ): Promise<ApiResponse<Petition>> {
    return apiClient.patch(`/petitions/${petitionId}/sections/${sectionKey}`, updates);
  }

  /**
   * Regenerate a specific petition section (full or partial)
   */
  static async regeneratePetitionSection(
    petitionId: string,
    sectionKey: string,
    payload: {
      userPrompt: string;
      partialContent?: string;
      context?: { beforeText?: string; afterText?: string };
    }
  ): Promise<ApiResponse<{ regenerated_content: string }>> {
    return apiClient.post(`/petitions/${petitionId}/sections/${sectionKey}/regenerate`, payload);
  }

  // ============================================
  // PETITION DRAFTING ENDPOINTS
  // ============================================

  /**
   * Get petition draft with section-based structure
   */
  static async getPetitionDraft(petitionId: string): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.get(`/petitions/drafting/${petitionId}/draft`);
  }

  /**
   * Update a petition section
   */
  static async updateDraftSection(
    petitionId: string,
    sectionId: string,
    content: string,
    userId?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.patch(`/petitions/drafting/${petitionId}/sections/${sectionId}`, {
      content,
      userId,
    });
  }

  /**
   * Approve a petition section
   */
  static async approveSection(
    petitionId: string,
    sectionId: string,
    userId: string,
    comment?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    const url = `/petitions/drafting/${petitionId}/sections/${sectionId}/approve`;
    const payload = { userId, comment };

    console.log('🌐 API Request - approveSection:', {
      url,
      payload,
      petitionId,
      sectionId,
    });

    try {
      const response = await apiClient.post(url, payload) as ApiResponse<PetitionDraft>;
      console.log('🌐 API Response - approveSection:', {
        success: response.success,
        hasData: !!response.data,
        response,
      });
      return response;
    } catch (error) {
      console.error('🌐 API Error - approveSection:', {
        url,
        error,
        errorMessage: (error as any)?.message,
        errorResponse: (error as any)?.response,
      });
      throw error;
    }
  }

  /**
   * Reject a petition section
   */
  static async rejectSection(
    petitionId: string,
    sectionId: string,
    userId: string,
    reason: string,
    comment?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections/${sectionId}/reject`, {
      userId,
      reason,
      comment,
    });
  }

  /**
   * Add a comment to a petition section
   */
  static async addSectionComment(
    petitionId: string,
    sectionId: string,
    userId: string,
    text: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections/${sectionId}/comment`, {
      userId,
      text,
    });
  }

  /**
   * Get approval status for all sections
   */
  static async getApprovalStatus(petitionId: string): Promise<ApiResponse<ApprovalStatus>> {
    return apiClient.get(`/petitions/drafting/${petitionId}/approval-status`);
  }

  /**
   * Regenerate a petition section using AI
   */
  static async regenerateDraftSection(
    petitionId: string,
    sectionId: string,
    userPrompt: string,
    partialContent?: string,
    context?: { beforeText?: string; afterText?: string },
    userId?: string
  ): Promise<ApiResponse<{ regeneratedContent: string; originalContent: string }>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections/${sectionId}/regenerate`, {
      userPrompt,
      partialContent,
      context,
      userId,
    });
  }

  /**
   * Auto-generate table of contents from petition sections
   */
  static async autoGenerateTableOfContents(
    petitionId: string,
    sectionId: string,
    userId?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections/${sectionId}/auto-generate-toc`, {
      userId,
    });
  }

  /**
   * Create a new section
   */
  static async createSection(
    petitionId: string,
    title: string,
    content?: string,
    userId?: string,
    afterSectionId?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections`, {
      title,
      content,
      userId,
      afterSectionId,
    });
  }

  /**
   * Delete a section
   */
  static async deleteSection(
    petitionId: string,
    sectionId: string,
    userId: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections/${sectionId}/delete`, {
      userId,
    });
  }

  /**
   * Rename a section
   */
  static async renameSection(
    petitionId: string,
    sectionId: string,
    newTitle: string,
    userId?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.patch(`/petitions/drafting/${petitionId}/sections/${sectionId}/rename`, {
      newTitle,
      userId,
    });
  }

  /**
   * Reorder sections
   */
  static async reorderSections(
    petitionId: string,
    sectionOrder: string[],
    userId?: string
  ): Promise<ApiResponse<PetitionDraft>> {
    return apiClient.post(`/petitions/drafting/${petitionId}/sections/reorder`, {
      sectionOrder,
      userId,
    });
  }

  /**
   * Perform AI-powered fact-check on petition content
   */
  static async factCheckContent(
    petitionId: string,
    content: string,
    sectionKey?: string
  ): Promise<ApiResponse<FactCheckResult>> {
    return apiClient.post(`/petitions/${petitionId}/fact-check`, { content, sectionKey });
  }
}

// ============================================
// PETITION DRAFTING TYPES
// ============================================

export enum SectionApprovalStatus {
  NEEDS_APPROVAL = 'needs_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  DRAFT = 'draft',
}

export interface SectionComment {
  userId: string;
  text: string;
  timestamp: string;
}

export interface PetitionSection {
  title: string;
  content: string;
  order: number;
  approvalStatus: SectionApprovalStatus;
  lastEditedBy?: string;
  lastEditedAt?: string;
  comments?: SectionComment[];
  version: number;
}

export interface PetitionDraft {
  petitionId: string;
  caseId: string;
  petitionType: string;
  status: string;
  sections: {
    [sectionKey: string]: PetitionSection;
  };
  tableOfContents: {
    [sectionKey: string]: {
      title: string;
      page?: number;
      order: number;
    };
  };
  metadata: {
    beneficiaryName?: string;
    visaType?: string;
    formNumber?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface ApprovalStatus {
  petitionId: string;
  overallStatus: 'all_approved' | 'partially_approved' | 'needs_approval' | 'has_rejections';
  sectionStatuses: {
    [sectionKey: string]: {
      status: SectionApprovalStatus;
      approvedBy?: string;
      approvedAt?: string;
      rejectedBy?: string;
      rejectedAt?: string;
      rejectionReason?: string;
      commentsCount: number;
    };
  };
  approvalProgress: {
    total: number;
    approved: number;
    rejected: number;
    needsApproval: number;
    draft: number;
  };
}

export default PetitionService;
